import type { BankAccount, ReserveDepositOptionResponse, TransactionDetail } from '@pf/contracts';
import {
  BadgeCheck,
  Banknote,
  CircleSlash,
  FileUp,
  Landmark,
  Loader2,
  ScanLine,
  ShieldQuestion,
  Upload,
  Wallet,
} from 'lucide-react';
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeposit,
  useActiveDeposit,
  useDepositOptions,
  useMarkPaid,
  useReleaseReservation,
  useReserveDeposit,
  useSubmitEvidence,
} from '@/lib/api/bff-queries';
import { usePolicy } from '@/lib/api/ops-queries';
import { bankInfo } from '@/lib/banks';
import { seconds } from '@/lib/clock';
import { formatDecimalString } from '@/lib/money';
import { cn } from '@/lib/utils';
import { CheckMatrix } from '@/components/domain/check-matrix';
import { CopyButton } from '@/components/domain/copy-button';
import { CountdownTimer } from '@/components/domain/countdown-timer';
import { MoneyDisplay } from '@/components/domain/money-display';
import { StateBadge } from '@/components/domain/state-badge';
import { StateTimeline } from '@/components/domain/state-timeline';
import { PageHeader, Section } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, Skeleton } from '@/components/ui/feedback';
import { Code } from '@/components/ui/primitives';

/**
 * Deposit — the participating website's cashier flow.
 *
 * The screen is the SAD §5/§7.1 sequence made visible, and its shape is dictated by one rule:
 * **no bank details until reserved.** The picker shows amounts and availability only, because
 * that is all `GET /deposit-options` returns; the withdrawer's account appears in the pay-to
 * panel only after `POST /deposit-options/reserve` has atomically claimed exactly one withdrawal.
 * Reversing that order would leak a stranger's bank details to anyone who opened the page.
 *
 * Everything after the reservation is a race against a clock, so every step shows the clock it is
 * racing: the reservation TTL, then the evidence deadline. Both come from policy rather than from
 * a hard-coded ten minutes.
 */
export function DepositPage(): ReactNode {
  const { t } = useTranslation();
  /*
   * The reservation survives a refresh, and has to.
   *
   * Reserving is an exclusive claim: it takes one withdrawal out of the order book and holds it
   * for this depositor until they pay, release it, or the TTL lapses. The payee is disclosed
   * exactly once, in the 201 from `POST /deposit-options/reserve` — `TransactionDetail` never
   * echoes it (SAD §5). Holding that response in component state *alone* meant a refresh lost the
   * whole reservation: the claim was still live server-side, the amount was gone from the book
   * because this customer held it, and the screen showed an empty picker. The deposit could not be
   * finished at all.
   *
   * So the server is the source of truth. `GET /bff/deposits/active` returns the in-flight deposit
   * and its payee; the reserve response is kept only as the immediate, no-round-trip path for the
   * click that created it.
   */
  const [justReserved, setJustReserved] = useState<ReserveDepositOptionResponse | null>(null);
  const active = useActiveDeposit();
  const resumed = active.data?.active ?? null;

  const activeId = justReserved?.id ?? resumed?.transaction.id ?? '';
  const deposit = useDeposit(activeId);
  const policy = usePolicy();

  // Prefer the live read; fall back to the reserve response for the moment before it lands.
  const transaction = deposit.data ?? resumed?.transaction ?? null;
  const payTo = justReserved?.pay_to ?? resumed?.pay_to ?? null;
  // The registered source account (masked) — null when the reservation carried none, and the
  // sender check is then not applicable, so the screen simply says nothing about it.
  const payFrom = justReserved?.pay_from ?? resumed?.pay_from ?? null;

  return (
    <>
      <PageHeader
        title={t('deposit.title')}
        description={t('deposit.description')}
        badge={
          transaction ? <StateBadge machine="deposit" status={transaction.status} /> : undefined
        }
      />

      {active.isLoading && activeId === '' ? (
        <DepositSkeleton />
      ) : activeId === '' ? (
        <AmountPicker onReserved={setJustReserved} />
      ) : deposit.isLoading ? (
        <DepositSkeleton />
      ) : deposit.isError ? (
        <ErrorState
          error={deposit.error}
          onRetry={() => {
            void deposit.refetch();
          }}
        />
      ) : transaction ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <ActiveDeposit
              transaction={transaction}
              payTo={payTo}
              payFrom={payFrom}
              reservationTtlMs={seconds(policy.data?.reservation_ttl_seconds ?? 600)}
              evidenceWindowMs={seconds(policy.data?.evidence_deadline_seconds ?? 600)}
              onStartOver={() => {
                // Drop the local copy *and* re-ask the server: the reservation has just been
                // released or finished, so "active" is now something else — or nothing.
                setJustReserved(null);
                void active.refetch();
              }}
            />
          </div>
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('history.detail.eventTimeline')}</CardTitle>
              </CardHeader>
              <CardContent>
                <StateTimeline events={transaction.events} dense />
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Step 1 — the order book
// ---------------------------------------------------------------------------------------------

function AmountPicker({
  onReserved,
}: {
  onReserved: (reservation: ReserveDepositOptionResponse) => void;
}): ReactNode {
  const { t } = useTranslation();
  const options = useDepositOptions();
  const reserve = useReserveDeposit();
  const [selected, setSelected] = useState<string | null>(null);

  if (options.isLoading) {
    return (
      <Section
        title={t('deposit.picker.sectionTitle')}
        description={t('deposit.picker.loadingDescription')}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </Section>
    );
  }

  if (options.isError) {
    return (
      <ErrorState
        error={options.error}
        onRetry={() => {
          void options.refetch();
        }}
      />
    );
  }

  const items = options.data?.options ?? [];
  const available = items.filter((o) => o.available);

  return (
    <div className="space-y-6">
      <Callout tone="info" title={t('deposit.picker.calloutTitle')}>
        {t('deposit.picker.calloutBody')}
      </Callout>

      <Section
        title={t('deposit.picker.sectionTitle')}
        description={t('deposit.picker.availabilityDescription', {
          available: available.length,
          total: items.length,
        })}
      >
        {available.length === 0 ? (
          <EmptyState
            icon={<Wallet className="size-5" aria-hidden="true" />}
            title={t('deposit.picker.emptyTitle')}
            description={t('deposit.picker.emptyDescription')}
          />
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((option) => {
              const isSelected = selected === option.amount;
              return (
                <li key={option.amount}>
                  <button
                    type="button"
                    disabled={!option.available || reserve.isPending}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSelected(option.amount);
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                      option.available
                        ? 'border-border bg-card hover:border-primary/50 hover:shadow-sm'
                        : 'cursor-not-allowed border-dashed border-border bg-muted/40 opacity-60',
                      isSelected && 'border-primary bg-accent shadow-sm ring-1 ring-primary',
                    )}
                  >
                    <span className="tnum text-lg font-semibold">
                      ฿{formatDecimalString(option.amount, 'THB', { decimalPlaces: 0 })}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 text-2xs font-medium',
                        option.available ? 'text-state-good' : 'text-muted-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'size-1.5 rounded-full',
                          option.available ? 'bg-state-good' : 'bg-muted-foreground',
                        )}
                        aria-hidden="true"
                      />
                      {option.available
                        ? t('deposit.picker.available')
                        : t('deposit.picker.outOfStock')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {reserve.isError ? (
        <ErrorState
          error={reserve.error}
          onRetry={() => {
            void options.refetch();
          }}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="lg"
          disabled={selected === null}
          loading={reserve.isPending}
          onClick={() => {
            if (selected === null) return;
            reserve.mutate({ value: selected, currency: 'THB' }, { onSuccess: onReserved });
          }}
        >
          <Banknote aria-hidden="true" />
          {selected === null
            ? t('deposit.picker.selectAmount')
            : t('deposit.picker.reserveButton', {
                amount: formatDecimalString(selected, 'THB', { decimalPlaces: 0 }),
              })}
        </Button>
        <p className="text-xs text-muted-foreground">{t('deposit.picker.reserveHint')}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Steps 2–5 — reserved → paid → verifying → done
// ---------------------------------------------------------------------------------------------

const STEPS = [
  { id: 'reserve', labelKey: 'deposit.steps.reserve' },
  { id: 'pay', labelKey: 'deposit.steps.transfer' },
  { id: 'slip', labelKey: 'deposit.steps.uploadSlip' },
  { id: 'done', labelKey: 'deposit.steps.credited' },
] as const;

function stepIndexFor(status: TransactionDetail['status']): number {
  switch (status) {
    case 'RESERVED':
      return 1;
    case 'AWAITING_EVIDENCE':
      return 2;
    case 'VERIFYING':
    case 'UNDER_REVIEW':
      return 2;
    case 'CONFIRMED':
    case 'CREDIT_PENDING':
      return 3;
    case 'COMPLETED':
      return 4;
    default:
      return 0;
  }
}

function Stepper({ status }: { status: TransactionDetail['status'] }): ReactNode {
  const { t } = useTranslation();
  const current = stepIndexFor(status);
  return (
    <ol
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
      aria-label={t('deposit.steps.ariaLabel')}
    >
      {STEPS.map((step, index) => {
        const done = index < current;
        const active = index === current - 1;
        return (
          <li key={step.id} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-5 items-center justify-center rounded-full text-2xs font-semibold',
                done
                  ? 'bg-state-good text-background'
                  : active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
              )}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <span className={cn(active ? 'font-medium text-foreground' : 'text-muted-foreground')}>
              {t(step.labelKey)}
              {active ? <span className="sr-only">{t('deposit.steps.currentStepSr')}</span> : null}
            </span>
            {index < STEPS.length - 1 ? (
              <span className="h-px w-4 bg-border sm:w-8" aria-hidden="true" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function PayToPanel({
  payTo,
  amount,
}: {
  payTo: BankAccount;
  amount: TransactionDetail['amount'];
}): ReactNode {
  const { t } = useTranslation();
  const bank = bankInfo(payTo.bank);
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div
        className="flex items-center gap-3 px-5 py-3.5 text-white"
        style={{ backgroundColor: bank.color }}
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-white/15 text-sm font-bold">
          {bank.monogram}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{bank.name}</span>
          <span className="block truncate text-xs opacity-90">{bank.nameTh}</span>
        </span>
      </div>
      <dl className="divide-y divide-border bg-card">
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <dt className="text-xs text-muted-foreground">{t('deposit.payTo.accountName')}</dt>
          <dd className="flex min-w-0 items-center gap-1 text-sm font-medium">
            <span className="truncate">{payTo.name}</span>
            <CopyButton value={payTo.name} label={t('deposit.payTo.copyAccountName')} />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <dt className="text-xs text-muted-foreground">{t('deposit.payTo.accountNumber')}</dt>
          <dd className="flex min-w-0 items-center gap-1">
            <span className="tnum truncate font-mono text-sm font-semibold">{payTo.number}</span>
            <CopyButton value={payTo.number} label={t('deposit.payTo.copyAccountNumber')} />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <dt className="text-xs text-muted-foreground">{t('deposit.payTo.exactAmount')}</dt>
          <dd className="flex min-w-0 items-center gap-1">
            <MoneyDisplay amount={amount} size="lg" />
            <CopyButton value={amount.value} label={t('deposit.payTo.copyAmount')} />
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * The account the transfer must come *from* — the reservation's registered payer, masked.
 *
 * Deliberately quieter than {@link PayToPanel} (no colored header, no copy buttons): the customer
 * owns this account and only needs to recognise it, not copy it. What earns it a panel at all is
 * the sender check — a transfer from any other account goes to manual review, and telling the
 * customer *before* they pay is the whole point of showing it.
 */
function PayFromPanel({ payFrom }: { payFrom: BankAccount }): ReactNode {
  const { t } = useTranslation();
  const bank = bankInfo(payFrom.bank);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
        <span
          className="flex size-9 items-center justify-center rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: bank.color }}
        >
          {bank.monogram}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{t('deposit.payFrom.title')}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {bank.name} · {bank.nameTh}
          </span>
        </span>
      </div>
      <dl className="divide-y divide-border">
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <dt className="text-xs text-muted-foreground">{t('deposit.payFrom.accountName')}</dt>
          <dd className="min-w-0 truncate text-sm font-medium">{payFrom.name}</dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <dt className="text-xs text-muted-foreground">{t('deposit.payFrom.accountNumber')}</dt>
          <dd className="tnum min-w-0 truncate font-mono text-sm font-semibold">
            {payFrom.number}
          </dd>
        </div>
      </dl>
      <p className="border-t border-border px-5 py-3 text-2xs leading-relaxed text-muted-foreground">
        {t('deposit.payFrom.note')}
      </p>
    </div>
  );
}

function SlipPicker({
  file,
  onSelect,
}: {
  file: File | null;
  onSelect: (file: File | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onSelect(event.target.files?.[0] ?? null);
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-border bg-muted/20 px-6 py-8 text-center',
        file && 'border-primary/50 bg-accent/40',
      )}
    >
      <span className="flex size-11 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
        <FileUp className="size-5" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {file ? file.name : t('deposit.slipUpload.placeholderTitle')}
        </p>
        <p className="mx-auto max-w-sm text-xs leading-relaxed text-muted-foreground">
          {file
            ? t('deposit.slipUpload.readySuffix', { size: (file.size / 1024).toFixed(0) })
            : t('deposit.slipUpload.instructions')}
        </p>
      </div>
      <input
        ref={inputRef}
        id="slip"
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        aria-label={t('deposit.slipUpload.chooseFile')}
        className="sr-only"
        onChange={onChange}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        <Upload aria-hidden="true" />
        {file ? t('deposit.slipUpload.chooseDifferentFile') : t('deposit.slipUpload.chooseFile')}
      </Button>
    </div>
  );
}

function SlipUpload({ transactionId }: { transactionId: string }): ReactNode {
  const { t } = useTranslation();
  const submit = useSubmitEvidence(transactionId);
  const [file, setFile] = useState<File | null>(null);

  return (
    <div className="space-y-3">
      <SlipPicker file={file} onSelect={setFile} />

      {submit.isError ? <ErrorState error={submit.error} /> : null}

      <Button
        className="w-full"
        size="lg"
        disabled={file === null}
        loading={submit.isPending}
        onClick={() => {
          if (file) submit.mutate(file);
        }}
      >
        <ScanLine aria-hidden="true" />
        {t('deposit.slipUpload.submitButton')}
      </Button>
      <p className="text-2xs leading-relaxed text-muted-foreground">
        {t('deposit.slipUpload.storageNote')}
      </p>
    </div>
  );
}

function ActiveDeposit({
  transaction,
  payTo,
  payFrom,
  reservationTtlMs,
  evidenceWindowMs,
  onStartOver,
}: {
  transaction: TransactionDetail;
  payTo: BankAccount | null;
  payFrom: BankAccount | null;
  reservationTtlMs: number;
  evidenceWindowMs: number;
  onStartOver: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const markPaid = useMarkPaid(transaction.id);
  const release = useReleaseReservation(transaction.id);
  /*
   * "I have paid" *is* the slip upload.
   *
   * The polite two-step version — claim first, upload "next" — is how deposits end up in manual
   * review with reason EVIDENCE_DEADLINE_BREACH: the depositor claims, closes the tab, and the
   * evidence clock runs out on nobody. So the one button on RESERVED opens the file picker, and
   * choosing the slip runs mark-paid then submit-evidence back to back. There is no separate
   * attach step and no second confirmation; the state machine underneath is unchanged.
   */
  const slipInputRef = useRef<HTMLInputElement>(null);
  const slipSubmit = useSubmitEvidence(transaction.id);
  const confirmWithSlip = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Allow re-choosing the same file after a failure — an unchanged value fires no change event.
    event.target.value = '';
    if (file === undefined) return;
    markPaid.mutate(undefined, {
      onSuccess: () => {
        slipSubmit.mutate(file);
      },
    });
  };

  return (
    <div className="space-y-6">
      <Stepper status={transaction.status} />

      {transaction.status === 'RESERVED' ? (
        <Section
          title={t('deposit.reserved.sectionTitle')}
          description={t('deposit.reserved.sectionDescription')}
          actions={
            transaction.deadline_at ? (
              <CountdownTimer
                deadline={transaction.deadline_at}
                totalMs={reservationTtlMs}
                label={t('deposit.reserved.countdownLabel')}
              />
            ) : undefined
          }
        >
          <div className="space-y-4">
            {payTo ? (
              <PayToPanel payTo={payTo} amount={transaction.amount} />
            ) : (
              <Callout tone="danger" title={t('deposit.reserved.noBankDetailsTitle')}>
                {t('deposit.reserved.noBankDetailsBody')}
              </Callout>
            )}
            {payFrom ? <PayFromPanel payFrom={payFrom} /> : null}
            <Callout tone="warning" title={t('deposit.reserved.exactAmountTitle')}>
              {t('deposit.reserved.exactAmountBody')}
            </Callout>
            <Callout tone="info" title={t('deposit.reserved.slipRequiredTitle')}>
              {t('deposit.reserved.slipRequiredBody')}
            </Callout>
            <input
              ref={slipInputRef}
              id="slip-confirm"
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              aria-label={t('deposit.reserved.markPaidButton')}
              className="sr-only"
              onChange={confirmWithSlip}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="lg"
                loading={markPaid.isPending || slipSubmit.isPending}
                onClick={() => {
                  slipInputRef.current?.click();
                }}
              >
                <FileUp aria-hidden="true" />
                {t('deposit.reserved.markPaidButton')}
              </Button>
              <Button
                variant="outline"
                loading={release.isPending}
                onClick={() => {
                  release.mutate(undefined, { onSuccess: onStartOver });
                }}
              >
                <CircleSlash aria-hidden="true" />
                {t('deposit.reserved.cancelButton')}
              </Button>
            </div>
            {markPaid.isError ? <ErrorState error={markPaid.error} /> : null}
            {release.isError ? <ErrorState error={release.error} /> : null}
          </div>
        </Section>
      ) : null}

      {transaction.status === 'AWAITING_EVIDENCE' ? (
        <Section
          title={t('deposit.awaitingEvidence.sectionTitle')}
          description={t('deposit.awaitingEvidence.sectionDescription', {
            attempt: transaction.evidence_attempts + 1,
          })}
          actions={
            transaction.deadline_at ? (
              <CountdownTimer
                deadline={transaction.deadline_at}
                totalMs={evidenceWindowMs}
                label={t('deposit.awaitingEvidence.countdownLabel')}
              />
            ) : undefined
          }
        >
          {slipSubmit.isPending ? (
            // The slip chosen at the confirm click is already on its way; showing the picker
            // again here would invite a second, competing upload.
            <div
              className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-10 text-center"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="size-6 animate-spin text-primary" aria-hidden="true" />
              <p className="text-sm font-medium">{t('deposit.awaitingEvidence.submittingTitle')}</p>
              <p className="mx-auto max-w-md text-xs leading-relaxed text-muted-foreground">
                {t('deposit.awaitingEvidence.submittingBody')}
              </p>
            </div>
          ) : (
            <>
              {slipSubmit.isError ? <ErrorState error={slipSubmit.error} /> : null}
              <SlipUpload transactionId={transaction.id} />
              {transaction.evidence_attempts > 0 ? (
                <Callout tone="warning" title={t('deposit.awaitingEvidence.mismatchTitle')}>
                  {t('deposit.awaitingEvidence.mismatchBody')}
                </Callout>
              ) : null}
            </>
          )}
        </Section>
      ) : null}

      {transaction.status === 'VERIFYING' ? (
        <Section
          title={t('deposit.verifying.sectionTitle')}
          description={t('deposit.verifying.sectionDescription')}
        >
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card px-6 py-12 text-center">
            <span className="relative flex size-12 items-center justify-center">
              <span
                className="absolute inset-0 animate-pulse-ring rounded-full bg-primary/30"
                aria-hidden="true"
              />
              <Loader2 className="size-6 animate-spin text-primary" aria-hidden="true" />
            </span>
            <div className="space-y-1" role="status" aria-live="polite">
              <p className="text-sm font-medium">{t('deposit.verifying.checkingTitle')}</p>
              <p className="mx-auto max-w-md text-xs leading-relaxed text-muted-foreground">
                {t('deposit.verifying.checkingBody')}
              </p>
            </div>
          </div>
        </Section>
      ) : null}

      {transaction.status === 'CONFIRMED' || transaction.status === 'CREDIT_PENDING' ? (
        <Section
          title={t('deposit.confirmed.sectionTitle')}
          description={t('deposit.confirmed.sectionDescription')}
        >
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-state-good/30 bg-state-good-bg px-6 py-10 text-center">
              <BadgeCheck className="size-9 text-state-good" aria-hidden="true" />
              <p className="text-sm font-semibold text-state-good">
                {transaction.status === 'CONFIRMED'
                  ? t('deposit.confirmed.confirmedText')
                  : t('deposit.confirmed.creditPendingText')}
              </p>
              <MoneyDisplay amount={transaction.amount} size="xl" />
            </div>
            {transaction.verification ? (
              <CheckMatrix checks={transaction.verification.checks} />
            ) : null}
          </div>
        </Section>
      ) : null}

      {transaction.status === 'COMPLETED' ? (
        <Section
          title={t('deposit.completed.sectionTitle')}
          description={t('deposit.completed.sectionDescription')}
        >
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-state-good/30 bg-state-good-bg px-6 py-10 text-center">
              <BadgeCheck className="size-9 text-state-good" aria-hidden="true" />
              <p className="text-sm font-semibold text-state-good">
                {t('deposit.completed.creditedText')}
              </p>
              <MoneyDisplay amount={transaction.amount} size="xl" />
              <p className="text-xs text-muted-foreground">
                {t('deposit.completed.referencePrefix')} <Code>{transaction.client_reference}</Code>
              </p>
            </div>
            <Button variant="outline" onClick={onStartOver}>
              {t('deposit.completed.newDepositButton')}
            </Button>
          </div>
        </Section>
      ) : null}

      {transaction.status === 'UNDER_REVIEW' ? (
        <Section
          title={t('deposit.underReview.sectionTitle')}
          description={t('deposit.underReview.sectionDescription')}
        >
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-state-attention/30 bg-state-attention-bg px-6 py-10 text-center">
              <ShieldQuestion className="size-9 text-state-attention" aria-hidden="true" />
              <p className="text-sm font-semibold text-state-attention">
                {t('deposit.underReview.title')}
              </p>
              <p className="mx-auto max-w-md text-xs leading-relaxed text-foreground/80">
                {t('deposit.underReview.body')}
              </p>
            </div>
            {transaction.verification ? (
              <CheckMatrix checks={transaction.verification.checks} />
            ) : null}
          </div>
        </Section>
      ) : null}

      {transaction.status === 'EXPIRED' || transaction.status === 'REJECTED' ? (
        <Section
          title={
            transaction.status === 'EXPIRED'
              ? t('deposit.expired.sectionTitle')
              : t('deposit.rejected.sectionTitle')
          }
          description={
            transaction.status === 'EXPIRED'
              ? t('deposit.expired.sectionDescription')
              : t('deposit.rejected.sectionDescription')
          }
        >
          <div className="space-y-4">
            <EmptyState
              icon={<CircleSlash className="size-5" aria-hidden="true" />}
              title={
                transaction.status === 'EXPIRED'
                  ? t('deposit.expired.emptyTitle')
                  : t('deposit.rejected.emptyTitle')
              }
              description={
                transaction.status === 'EXPIRED'
                  ? t('deposit.expired.emptyDescription')
                  : t('deposit.rejected.emptyDescription')
              }
              action={
                <Button variant="outline" onClick={onStartOver}>
                  {t('deposit.startNewButton')}
                </Button>
              }
            />
          </div>
        </Section>
      ) : null}

      <Section
        title={t('deposit.whereMoneyWent.sectionTitle')}
        description={t('deposit.whereMoneyWent.sectionDescription')}
      >
        <ul className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
          {[
            {
              icon: Wallet,
              label: t('deposit.whereMoneyWent.you.label'),
              body: t('deposit.whereMoneyWent.you.body'),
            },
            {
              icon: Landmark,
              label: t('deposit.whereMoneyWent.theirBank.label'),
              body: t('deposit.whereMoneyWent.theirBank.body'),
            },
            {
              icon: BadgeCheck,
              label: t('deposit.whereMoneyWent.yourBalance.label'),
              body: t('deposit.whereMoneyWent.yourBalance.body'),
            },
          ].map(({ icon: Icon, label, body }) => (
            <li
              key={label}
              className="flex items-start gap-2.5 rounded-lg border border-border p-3"
            >
              <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>
                <span className="block font-medium text-foreground">{label}</span>
                <span className="block leading-relaxed">{body}</span>
              </span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function DepositSkeleton(): ReactNode {
  const { t } = useTranslation();
  return (
    <div
      className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]"
      role="status"
      aria-label={t('deposit.skeleton.ariaLabel')}
    >
      <div className="space-y-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-11 w-48" />
      </div>
      <Skeleton className="h-72 w-full" />
    </div>
  );
}
